import {collection, deleteDoc, doc, onSnapshot, updateDoc, type Unsubscribe} from 'firebase/firestore';
import {useEffect, useState} from 'react';
import {firebaseAuth, firebaseFirestore} from '../setup/firebase';
import {GdriveTokenStatus} from '../constants/gdriveTokenStatus';

export interface GdriveTokenItem {
  id: string;
  name: string;
  email: string;
  createdAt: number;
  status: GdriveTokenStatus;
  errorMessage?: string;
}

export default function useGdriveTokens() {
  const [uid, setUid] = useState<string | null>(null);
  const [tokens, setTokens] = useState<GdriveTokenItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubAuth = firebaseAuth.onAuthStateChanged((user) => {
      setUid(user?.uid ?? null);
    });
    return () => {
      unsubAuth();
    };
  }, []);

  useEffect(() => {
    if (!uid) {
      setTokens([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const collRef = collection(firebaseFirestore, 'user', uid, 'gdriveToken');
    const unsub: Unsubscribe = onSnapshot(
      collRef,
      (snap) => {
        const list: GdriveTokenItem[] = [];
        snap.docs.forEach((d) => {
          const data = d.data();
          list.push({
            id: d.id,
            name: typeof data.name === 'string' ? data.name : '',
            email: typeof data.email === 'string' ? data.email : '',
            createdAt: typeof data.createdAt === 'number' ? data.createdAt : 0,
            status: (data.status as GdriveTokenStatus) ?? GdriveTokenStatus.COMPLETE,
            errorMessage: typeof data.errorMessage === 'string' ? data.errorMessage : undefined,
          });
        });
        setTokens(list);
        setLoading(false);
      },
      (err) => {
        console.error('useGdriveTokens onSnapshot error', err);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [uid]);

  const deleteToken = async (tokenId: string) => {
    if (!uid) return;
    const docRef = doc(firebaseFirestore, 'user', uid, 'gdriveToken', tokenId);
    await deleteDoc(docRef);
  };

  const updateName = async (tokenId: string, name: string) => {
    if (!uid) return;
    const docRef = doc(firebaseFirestore, 'user', uid, 'gdriveToken', tokenId);
    await updateDoc(docRef, {name});
  };

  const completedTokens = tokens.filter((t) => t.status === GdriveTokenStatus.COMPLETE);
  const pendingTokens = tokens.filter(
    (t) => t.status === GdriveTokenStatus.PENDING || t.status === GdriveTokenStatus.PROCESSING,
  );
  const errorTokens = tokens.filter((t) => t.status === GdriveTokenStatus.ERROR);

  return {tokens, completedTokens, pendingTokens, errorTokens, loading, deleteToken, updateName};
}
